import { Lens, useLens } from "@hookform/lenses";
import { useFieldArray } from "@hookform/lenses/rhf";
import { zodResolver } from "@hookform/resolvers/zod";
import { Delete } from "@mui/icons-material";
import {
  Button,
  DialogContent,
  DialogActions,
  MenuItem,
  Box,
  IconButton,
  Typography,
} from "@mui/material";
import {
  ReactElement,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { useForm } from "react-hook-form";
import z from "zod";

import { FlexBox } from "~frontend/components/FlexBox";
import Select from "~frontend/components/forms/FormSelect";
import {
  RhfErrorBanner,
  RhfTextField,
} from "~frontend/components/forms/react-hook-form-components";
import { useFeatureFlag } from "~frontend/providers/LoggedInUserProvider/context";
import { palette } from "~frontend/theme";
import { useErrorMessageFormatter } from "~frontend/util/errors";

import { EditIntegrationsList } from "./EditIntegrationsList";
import {
  EditTagGroupFormSchema,
  EditTagGroupFormType,
  NgpVanIntegration,
  TagType,
} from "./types";

export default function EditTagForm({
  onTypeChange,
  initialName,
  initialTags,
  onSave,
  onCancelClick,
  vanIntegrations,
  allOtherTagNames,
}: {
  onTypeChange?: (name: string, newType: "flat" | "singleSelect") => void;
  initialName: string;
  initialTags?: EditTagGroupFormType["tags"];
  onSave: (formData: EditTagGroupFormType) => Promise<void>;
  onCancelClick: () => void;
  vanIntegrations: NgpVanIntegration[];
  allOtherTagNames: string[];
}): ReactElement {
  const {
    getValues,
    control,
    formState: { isSubmitting },
    handleSubmit,
    setError,
    subscribe,
  } = useForm({
    resolver: zodResolver(
      EditTagGroupFormSchema.superRefine((data, ctx) => {
        if (data.name && allOtherTagNames.includes(data.name.trim())) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "A tag with this name already exists",
            path: ["name"],
          });
        }

        const tagNames = new Set();

        data.tags.forEach((tag, index) => {
          const name = tag.name.trim();
          if (tagNames.has(name)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: "Options must have unique names",
              path: ["tags", index, "name"],
            });
          } else {
            tagNames.add(name);
          }
        });

        return true;
      })
    ),
    values: {
      name: initialName,
      tags: initialTags || [],
    },
  });

  const tagGroupsEnabled = useFeatureFlag("tagGroups");

  const editTagsList = useRef<EditTagsListHandle>(null);

  const lens = useLens({ control });
  const tagsLens = lens.focus("tags");

  const [hasIntegrations, setHasIntegrations] = useState(false);
  useEffect(() => {
    const callback = subscribe({
      name: "tags",
      formState: {
        values: true,
      },
      callback: ({ values }) => {
        // ?. on integrations is required because of a transient react-hook-form
        // state where when a tag is removed, there is a brief moment where
        // its entry is an empty object instead of being removed from the array.
        //
        // https://github.com/react-hook-form/react-hook-form/issues/12791
        setHasIntegrations(
          values.tags.some(tag => tag.integrations?.length > 0)
        );
      },
    });

    return () => callback();
  }, [subscribe]);

  const formatErrorMessage = useErrorMessageFormatter();
  async function handleSave(values: EditTagGroupFormType) {
    try {
      await onSave(values);
    } catch (error) {
      setError("root", { message: formatErrorMessage(error) });
    }
  }

  return (
    <form onSubmit={handleSubmit(handleSave)}>
      <DialogContent>
        <RhfErrorBanner control={control} />
        <RhfTextField
          name="name"
          label="Tag Name"
          control={control}
          fullWidth
        />
        {onTypeChange && tagGroupsEnabled && (
          <Select
            label="Tag Type"
            fullWidth
            value="singleSelect"
            onChange={evt => {
              const newType = evt.target.value as TagType;
              if (newType !== "singleSelect") {
                const name = getValues("name");
                onTypeChange(name as string, newType);
              }
            }}
          >
            <MenuItem value="flat">Flat Tag</MenuItem>
            <MenuItem value="singleSelect">Single Select</MenuItem>
          </Select>
        )}
        <EditTagsList
          lens={tagsLens}
          vanIntegrations={vanIntegrations}
          ref={editTagsList}
        />

        {hasIntegrations && (
          <Typography variant="body1" sx={{ mt: 2 }}>
            Note: If an admin or texter deletes a tag after applying it to a
            thread, the tag will only be removed from Scale to Win’s data, and
            will not remove the mapped VAN action.
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ justifyContent: "space-between", px: 3, pb: 2 }}>
        <Button
          variant="contained"
          color="tertiary"
          onClick={() => editTagsList.current?.addNewTag()}
        >
          + Add Single Select Option
        </Button>
        <FlexBox gap={1}>
          <Button
            onClick={onCancelClick}
            color="tertiary"
            variant="contained"
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            color="primary"
            variant="contained"
            disabled={isSubmitting}
            loading={isSubmitting}
          >
            Save
          </Button>
        </FlexBox>
      </DialogActions>
    </form>
  );
}

type EditTagsListHandle = {
  addNewTag: () => void;
};

function EditTagsList({
  lens,
  vanIntegrations,
  ref,
}: {
  lens: Lens<EditTagGroupFormType["tags"]>;
  vanIntegrations: NgpVanIntegration[];
  ref: React.RefObject<EditTagsListHandle | null>;
}): ReactElement {
  const fieldHelpers = useFieldArray(lens.interop());

  useImperativeHandle(ref, () => ({
    addNewTag: () => {
      fieldHelpers.append({
        name: "",
        integrations: [],
      });
    },
  }));

  return (
    <Box>
      {lens.map(fieldHelpers.fields, (tagValue, l, index) => (
        <Box
          key={tagValue.id}
          sx={{ borderBottom: `1px solid ${palette.lightGray}`, pb: 2, mb: 1 }}
        >
          <FlexBox>
            <Box sx={{ flex: 1 }}>
              <RhfTextField
                {...l.focus("name").interop()}
                fullWidth
                label={`Single Select Option ${index + 1}`}
              />
            </Box>
            <Box>
              <IconButton
                onClick={() => fieldHelpers.remove(index)}
                sx={{
                  height: "40px",
                  mt: 3,
                  ml: 2,
                }}
              >
                <Delete />
              </IconButton>
            </Box>
          </FlexBox>
          <Box sx={{ marginLeft: "24px" }}>
            <EditIntegrationsList
              lens={l.focus("integrations")}
              vanIntegrations={vanIntegrations}
              displayAddButton
            />
          </Box>
        </Box>
      ))}
    </Box>
  );
}
